import React, { useState } from 'react';
import { ArrowRightIcon, CompassIcon, HeartPulseIcon, PillIcon, PresentationIcon, SlidersHorizontalIcon, SparklesIcon } from 'lucide-react';
import { Section } from '../Section';
import { InView } from '../InView';
import { SectionHeadline } from '../SectionHeadline';
import { AnimatePresence } from 'framer-motion';
import { ImageWindow } from '../ImageWindow';
import { learnOutputs, peerChoices } from '../../../data/ferringConcept';
import { signalLoop } from '../../../data/deck';

/** One icon per learnOutputs entry, in order. */
const MEASURE_ICONS = [PillIcon, HeartPulseIcon, SlidersHorizontalIcon, CompassIcon, SparklesIcon];
const PROOF_REPORT_IMAGE = 'https://cdn.magicpatterns.com/uploads/eGk6j1UWJCUgZNymwvciRe/Screenshot_2026-10-22_at_13.09.21.png';
export function SignalsSection() {
  const [caseOpen, setCaseOpen] = useState(false);
  const maxShare = Math.max(...peerChoices.map(choice => choice.share));
  return <Section id="signals" data-id="element-530" data-mp="21ahjl1ydus6">
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16" data-id="element-531" data-mp="3e6g7hy05cvvu">
        <div data-id="element-532" data-mp="397mrnyxe0ssi">
          <InView data-id="element-533" data-mp="396tqddh76vhr">
            <SectionHeadline lead="More engagement." follow="More insight." data-id="element-534" data-mp="396k1jt7w2udn" />
          </InView>
          <InView delay={0.1} data-id="element-535" data-mp="349bg9jrg2a86">
            <p className="mt-8 max-w-[38ch] text-[19px] font-semibold leading-[1.35] text-[var(--muted)]" data-id="element-536" data-mp="34act9ts0t6sj">
              The Snak doesn’t just communicate. It shows Ferring how fertility specialists think, choose and dose.
            </p>
          </InView>
          <InView delay={0.2} className="mt-12" data-id="element-537" data-mp="3rxl1ajgu2ty9">
            <div className="flex flex-wrap items-center gap-2" data-id="element-538" data-mp="3rzxn57pppogl">
              {signalLoop.map((step, i) => <React.Fragment key={step} data-id="element-539" data-mp="3i81fb4xtqjz6" data-mpi={i}>
                  <span className="rounded-full bg-white px-4 py-2 text-[12px] font-semibold text-[var(--fg)] ring-1 ring-[var(--fg)]/10" data-id="element-540" data-mp="3i6zzkn5v59b4">
                    {step}
                  </span>
                  {i < signalLoop.length - 1 && <ArrowRightIcon className="h-3.5 w-3.5 text-[var(--fg)]/40" aria-hidden="true" data-id="element-541" data-mp="ephwet11cqh1" />}
                </React.Fragment>)}
            </div>
            <p className="mt-8 flex items-center gap-3 text-[24px] font-extrabold leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-542" data-mp="2dxgj7ti6pr9m">
              <span aria-hidden="true" className="h-[2px] w-8 rounded-full bg-[var(--brand)]" data-id="element-543" data-mp="2486pk97p00je" />
              Learn what works. Do more of it.
            </p>
            <button type="button" onClick={() => setCaseOpen(true)} className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-[var(--fg)] px-6 py-3.5 text-[14px] font-bold text-white shadow-[0_18px_40px_-20px_rgba(14,27,51,0.65)] transition-colors hover:bg-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-544" data-mp="1q7dpl8ymgd8w">
              <PresentationIcon className="h-4 w-4" aria-hidden="true" data-id="element-545" data-mp="3fnd4l6ox8wwg" />
              Proof: a Snak data report
            </button>
          </InView>
        </div>

        <InView delay={0.15} y={26} data-id="element-546" data-mp="13y5lxo1oln9n">
          <figure className="rounded-[30px] bg-[var(--fg)] p-6 text-white shadow-[0_50px_90px_-50px_rgba(14,27,51,0.8)] sm:p-8" data-id="element-547" data-mp="z1f3iu2emfl2">
            <div className="flex items-center justify-between" data-id="element-548" data-mp="1dquvgddo8r2c">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-lighter)]" data-id="element-549" data-mp="18zmkve0luznl">
                Campaign intelligence
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40" data-id="element-550" data-mp="1msuch5oorv5a">Illustrative</span>
            </div>

            <div className="mt-6" data-id="element-551" data-mp="1wpgvdoxgcc4o">
              <p className="text-[13px] font-semibold text-white/70" data-id="element-552" data-mp="1wof9sqit878p">Starting dose chosen for Patient A</p>
              <ul className="mt-3 space-y-2" data-id="element-553" data-mp="1rqm4dzpbzh9h">
                {peerChoices.map(choice => <li key={choice.label} className="flex items-center gap-3" data-id="element-554" data-mp="2bfwx55tdr54">
                    <span className="w-16 shrink-0 text-[12px] font-semibold tabular-nums text-white/70" data-id="element-555" data-mp="3ti8ywb8fluap">{choice.label}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/10" data-id="element-556" data-mp="2kk0tky6ox2sn">
                      <span className={`block h-full rounded-full ${choice.you ? 'bg-[var(--brand-light)]' : 'bg-white/35'}`} style={{
                    width: `${choice.share / maxShare * 100}%`
                  }} data-id="element-557" data-mp="2phslhs7c4owu" />
                    </span>
                    <span className="w-9 shrink-0 text-right text-[12px] font-bold tabular-nums text-white" data-id="element-558" data-mp="21v2ioon5wqb6">{choice.share}%</span>
                  </li>)}
              </ul>
            </div>

            <ol className="mt-8 border-t border-white/10" data-id="element-559" data-mp="2ato0uioq23bf">
              {learnOutputs.map((output, i) => {
              const Icon = MEASURE_ICONS[i % MEASURE_ICONS.length];
              return <li key={output} className="flex items-center gap-4 border-b border-white/10 py-3.5 last:border-b-0" data-id="element-560" data-mp="1j2rum0x62wx4" data-mpi={i}>
                    <span className="w-5 text-[10px] font-semibold tabular-nums text-white/35" data-id="element-561" data-mp="1n2sdbxratu9z">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-[var(--brand-lighter)]" data-id="element-562" data-mp="1amsl59flslvt">
                      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-563" data-mp="11luppfj8dmou" />
                    </span>
                    <span className="text-[14px] leading-snug text-white/85" data-id="element-564" data-mp="wm82cd9ouq2b">{output}</span>
                  </li>;
            })}
            </ol>
            <figcaption className="mt-4 text-[11px] leading-relaxed text-white/45" data-id="element-565" data-mp="rugn2yvzg6rh">
              Signals inform the next wave and sharpen activation. Peer data shown is illustrative, not real.
            </figcaption>
          </figure>
        </InView>
      </div>
      <AnimatePresence data-id="element-566" data-mp="dw9yk9cxbj4h">
        {caseOpen && <ImageWindow key="proof" src={PROOF_REPORT_IMAGE} alt="Snak data report for the Seretide Kenya teaser: what needs doing and by whom, with channel and CRM-code filters" title="Proof case · Snak data report" onClose={() => setCaseOpen(false)} data-id="element-567" data-mp="dxupzq3xyly4" />}
      </AnimatePresence>
    </Section>;
}